// Layer legends for the layer menu's info popover. Layers are server-rendered PNG tiles,
// so the client has no style to build a legend from: GET Layer/Legend returns one per
// layer, and this turns its entries into swatches the popover draws, matching the
// hand-coded legends (the deer movement ramp, Unit Info's land swatches).
//
// Only curated legends with entries are shown. The summary and notes are written for AI
// agents (field names, query advice), so the popover leaves them out.

const NEUTRAL = '#9A9999'
const LINE_WIDTH = 3
// Area fills are 15-50% opaque on the map, which as a 14px square reads as nearly blank.
// Swatches draw them at least this opaque, over a light ground (see legend.scss).
export const MIN_FILL_OPACITY = 0.6

const isNum = v => typeof v === 'number' && Number.isFinite(v)

const fillOpacity = opacity => (isNum(opacity) ? Math.max(opacity, MIN_FILL_OPACITY) : opacity)

/** A #rgb or #rrggbb colour at an opacity, as rgba(); anything else is returned as is. */
export const withOpacity = (color, opacity) => {
  if (!color || !isNum(opacity) || opacity >= 1) return color
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color)
  if (!hex) return color
  const h = hex[1].length === 3 ? hex[1].split('').map(c => c + c).join('') : hex[1]
  const [r, g, b] = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16))
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, opacity)})`
}

const range = (min, max) => {
  if (isNum(min) && isNum(max)) return `${min.toLocaleString()}–${max.toLocaleString()}`
  if (isNum(min)) return `${min.toLocaleString()}+`
  if (isNum(max)) return `Up to ${max.toLocaleString()}`
  return null
}

/** What an entry's row says: its label, else its value or range, else its colour's name. */
export const entryLabel = entry => entry.label ||
  (entry.value !== undefined && entry.value !== null ? String(entry.value) : null) ||
  range(entry.min, entry.max) || entry.colorName || ''

/**
 * One entry as a swatch: { shape, label, ...style }. Shapes are square (fill, outline,
 * hatch), line, marker, dot (icon), and ramp, which only rasterRamp makes.
 */
export const swatchFor = entry => {
  const label = entryLabel(entry),
    color = entry.color || NEUTRAL,
    stroke = entry.stroke

  switch (entry.kind) {
    case 'fill':
      return {
        shape: 'square', label,
        background: withOpacity(color, fillOpacity(entry.opacity)),
        border: stroke ? `1px solid ${stroke}` : null
      }
    case 'outline':
      return { shape: 'square', label, hollow: true, background: 'transparent', border: `2px ${entry.dashed ? 'dashed' : 'solid'} ${stroke || color}` }
    case 'hatch': {
      const ink = withOpacity(color, fillOpacity(entry.opacity))
      return {
        shape: 'square', label,
        background: `repeating-linear-gradient(45deg, ${ink} 0 2px, transparent 2px 5px)`,
        border: `1px solid ${stroke || color}`
      }
    }
    case 'line':
      return {
        shape: 'line', label,
        color: withOpacity(color, entry.opacity),
        width: LINE_WIDTH,
        dashed: !!entry.dashed,
        casing: entry.casing || null
      }
    case 'marker':
      return {
        shape: 'marker', label,
        marker: ['square', 'triangle', 'diamond'].includes(entry.shape) ? entry.shape : 'circle',
        background: withOpacity(color, fillOpacity(entry.opacity)),
        stroke: stroke || null
      }
    case 'raster':
      return { shape: 'square', label, background: withOpacity(color, entry.opacity), border: null }
    case 'icon':
    default:
      return { shape: 'dot', label, background: NEUTRAL, border: null }
  }
}

/**
 * The raster entries as one ramp: a gradient through their colours at their quantities
 * (evenly spaced when they have none), labelled at each stop.
 */
export const rasterRamp = entries => {
  const stops = entries.filter(e => e.color)
  if (!stops.length) return null
  const qs = stops.map(e => e.quantity),
    numeric = qs.every(isNum),
    sorted = numeric ? stops.slice().sort((a, b) => a.quantity - b.quantity) : stops,
    lo = numeric ? sorted[0].quantity : 0,
    span = numeric ? sorted[sorted.length - 1].quantity - lo : 0,
    at = (e, i) => sorted.length === 1 ? 0
      : numeric && span > 0 ? (e.quantity - lo) / span
        : i / (sorted.length - 1),
    points = sorted.map((e, i) => ({ color: withOpacity(e.color, e.opacity), at: at(e, i), label: e.label || (isNum(e.quantity) ? String(e.quantity) : '') }))
  return {
    shape: 'ramp',
    gradient: points.length === 1 ? points[0].color
      : `linear-gradient(to right, ${points.map(p => `${p.color} ${Math.round(p.at * 1000) / 10}%`).join(', ')})`,
    stops: points.map(p => ({ label: p.label, at: p.at }))
  }
}

/**
 * A Layer/Legend item as the popover shows it, or null when there is nothing to show
 * (not curated, or no entries). Raster entries collapse into one ramp, in the place of
 * the first.
 */
export const legendForDisplay = legend => {
  if (!legend || !legend.curated) return null
  const entries = legend.entries || []
  if (!entries.length) return null
  // A raster of classes (a value per entry: NLCD land cover) is a square per class; only a
  // continuous one (canopy percent, a colour map by quantity) is a ramp.
  const rasters = entries.filter(e => e.kind === 'raster'),
    classes = rasters.some(e => e.value != null),
    ramp = rasters.length > 1 && !classes ? rasterRamp(rasters) : null,
    swatches = []
  entries.forEach(e => {
    if (ramp && e.kind === 'raster') {
      if (e === rasters[0]) swatches.push(ramp)
      return
    }
    swatches.push(swatchFor(e))
  })
  return {
    single: swatches.length === 1 && swatches[0].shape !== 'ramp',
    swatches
  }
}
